import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

const STORAGE_KEY = "praccel-display-scale";

const MIN_SCALE = 0.5;
const MAX_SCALE = 1.2;
const STEP = 0.1;
const DEFAULT_SCALE = 1;

const DisplayScaleContext = createContext(null);

const clampScale = (value) => {
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, value));
};

const readStoredScale = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);

    if (!stored) {
      return DEFAULT_SCALE;
    }

    const parsed = Number(stored);

    if (!Number.isFinite(parsed)) {
      return DEFAULT_SCALE;
    }

    return clampScale(parsed);
  } catch {
    return DEFAULT_SCALE;
  }
};

export const DisplayScaleProvider = ({ children }) => {
  const [scale, setScale] = useState(readStoredScale);

  useEffect(() => {
  document.documentElement.style.setProperty(
    "--praccel-display-scale",
    scale
  );
}, [scale]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(scale));
    } catch {
      // Ignore localStorage failures.
    }
  }, [scale]);

  const setDisplayScale = useCallback((nextScale) => {
    setScale(clampScale(Number(nextScale)));
  }, []);

  const zoomIn = useCallback(() => {
    setScale((current) => clampScale(current + STEP));
  }, []);

  const zoomOut = useCallback(() => {
    setScale((current) => clampScale(current - STEP));
  }, []);

  const resetScale = useCallback(() => {
    setScale(DEFAULT_SCALE);
  }, []);

  const scalePercentage = Math.round(scale * 100);

  const value = useMemo(
    () => ({
      scale,
      scalePercentage,
      minScale: MIN_SCALE,
      maxScale: MAX_SCALE,
      step: STEP,
      setDisplayScale,
      zoomIn,
      zoomOut,
      resetScale,
    }),
    [
      scale,
      scalePercentage,
      setDisplayScale,
      zoomIn,
      zoomOut,
      resetScale,
    ],
  );

  return (
    <DisplayScaleContext.Provider value={value}>
      {children}
    </DisplayScaleContext.Provider>
  );
};

export const useDisplayScale = () => {
  const context = useContext(DisplayScaleContext);

  if (!context) {
    throw new Error(
      "useDisplayScale must be used inside DisplayScaleProvider",
    );
  }

  return context;
};